import React, { useMemo, useState } from "react";
import ListContactsModal from "./ListContactsModal";

export default function RecipientsStep({
  form,
  updateForm,
  contacts,
  lists,
  tags,
  selectedCount
}) {
  const [tab, setTab] = useState("include");
  const [search, setSearch] = useState("");
  const [openList, setOpenList] = useState(null);

  const filteredContacts = useMemo(() => {
    const q = search.toLowerCase();

    return contacts.filter((contact) => {
      return (
        (contact.name || "").toLowerCase().includes(q) ||
        (contact.email || "").toLowerCase().includes(q) ||
        (contact.company || "").toLowerCase().includes(q)
      );
    });
  }, [contacts, search]);

  function toggleArray(key, value) {
    const current = form[key] || [];

    if (current.includes(value)) {
      updateForm(
        key,
        current.filter((item) => item !== value)
      );
    } else {
      updateForm(key, [...current, value]);
    }
  }

  function isChecked(key, value) {
    return (form[key] || []).includes(value);
  }

  // --- contacts that belong to a given list (by list name) ---
  function contactsInList(list) {
    return contacts.filter(
      (c) => c.list === list.name || (c.lists || []).includes(list.name)
    );
  }

  const excludedContacts = form.excludedContacts || [];

  function toggleExcludeContact(id) {
    toggleArray("excludedContacts", id);
  }

  function enableAllInList(list) {
    const ids = contactsInList(list).map((c) => c._id);
    updateForm(
      "excludedContacts",
      (form.excludedContacts || []).filter((id) => !ids.includes(id))
    );
  }

  function disableAllInList(list) {
    const ids = contactsInList(list).map((c) => c._id);
    updateForm(
      "excludedContacts",
      Array.from(new Set([...(form.excludedContacts || []), ...ids]))
    );
  }

  // small stylish list icon
  const ListIcon = () => (
    <span className="sx-list-ic" aria-hidden="true">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="14" height="3" rx="1.5" fill="currentColor" opacity=".9" />
        <rect x="3" y="10.5" width="18" height="3" rx="1.5" fill="currentColor" opacity=".6" />
        <rect x="3" y="17" width="11" height="3" rx="1.5" fill="currentColor" opacity=".35" />
      </svg>
    </span>
  );

  function renderListCard(list, key, clickable) {
    const count = contactsInList(list).length;
    return (
      <div
        key={list._id}
        className={`sx-list-card ${clickable ? "clickable" : ""}`}
        onClick={clickable ? () => setOpenList(list) : undefined}
        role={clickable ? "button" : undefined}
      >
        <label className="sx-list-check" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isChecked(key, list.name)}
            onChange={() => toggleArray(key, list.name)}
          />
        </label>

        <ListIcon />
        <span className="sx-list-name">{list.name}</span>
        <span className="sx-list-count">
          {count} {count === 1 ? "contact" : "contacts"}
        </span>
        {clickable && <span className="sx-list-open">Manage ›</span>}
      </div>
    );
  }

  const modalListContacts = openList ? contactsInList(openList) : [];

  return (
    <div className="sx-recipients-layout">
      <div className="sx-recipients-header">
        <div>
          <h2>Recipients</h2>
          <p>
            Choose who should receive this campaign. Include or exclude contacts
            by list, tag, or individual contact.
          </p>
        </div>

        <div className="sx-recipient-counter">
          <strong>{selectedCount}</strong>
          <span>contacts selected</span>
        </div>
      </div>

      <div className="sx-recipient-mode">
        <button
          type="button"
          className={form.recipientMode === "all" ? "active" : ""}
          onClick={() => updateForm("recipientMode", "all")}
        >
          Send to all active contacts
        </button>

        <button
          type="button"
          className={form.recipientMode === "custom" ? "active" : ""}
          onClick={() => updateForm("recipientMode", "custom")}
        >
          Choose manually
        </button>
      </div>

      {form.recipientMode === "custom" && (
        <>
          <div className="sx-recipient-tabs">
            <button
              type="button"
              className={tab === "include" ? "active" : ""}
              onClick={() => setTab("include")}
            >
              Include
            </button>

            <button
              type="button"
              className={tab === "exclude" ? "active" : ""}
              onClick={() => setTab("exclude")}
            >
              Exclude
            </button>
          </div>

          {tab === "include" && (
            <div className="sx-recipient-grid">
              <div className="sx-recipient-box">
                <h3>Include Lists</h3>

                {lists.length === 0 ? (
                  <p>No lists found.</p>
                ) : (
                  <div className="sx-list-cards">
                    {lists.map((list) =>
                      renderListCard(list, "selectedLists", true)
                    )}
                  </div>
                )}
              </div>

              <div className="sx-recipient-box">
                <h3>Include Tags</h3>

                {tags.length === 0 ? (
                  <p>No tags found.</p>
                ) : (
                  tags.map((tag) => (
                    <label key={tag._id}>
                      <input
                        type="checkbox"
                        checked={isChecked("selectedTags", tag.name)}
                        onChange={() => toggleArray("selectedTags", tag.name)}
                      />
                      <span>{tag.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === "exclude" && (
            <div className="sx-recipient-grid">
              <div className="sx-recipient-box">
                <h3>Exclude Lists</h3>

                {lists.length === 0 ? (
                  <p>No lists found.</p>
                ) : (
                  <div className="sx-list-cards">
                    {lists.map((list) =>
                      renderListCard(list, "excludedLists", false)
                    )}
                  </div>
                )}
              </div>

              <div className="sx-recipient-box">
                <h3>Exclude Tags</h3>

                {tags.length === 0 ? (
                  <p>No tags found.</p>
                ) : (
                  tags.map((tag) => (
                    <label key={tag._id}>
                      <input
                        type="checkbox"
                        checked={isChecked("excludedTags", tag.name)}
                        onChange={() => toggleArray("excludedTags", tag.name)}
                      />
                      <span>{tag.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="sx-contact-select-box">
            <div className="sx-contact-select-head">
              <h3>
                {tab === "include"
                  ? "Include Individual Contacts"
                  : "Exclude Individual Contacts"}
                <span className="sx-contact-count">
                  {contacts.length} {contacts.length === 1 ? "contact" : "contacts"}
                </span>
              </h3>

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts..."
              />
            </div>

            <div className="sx-contact-select-list">
              {filteredContacts.map((contact) => {
                const key =
                  tab === "include" ? "selectedContacts" : "excludedContacts";

                return (
                  <label key={contact._id} className="sx-contact-row">
                    <input
                      type="checkbox"
                      checked={isChecked(key, contact._id)}
                      onChange={() => toggleArray(key, contact._id)}
                    />

                    <div>
                      <b>{contact.name || contact.email}</b>
                      <span>{contact.email}</span>
                    </div>
                  </label>
                );
              })}

              {filteredContacts.length === 0 && (
                <p className="muted">No contacts found.</p>
              )}
            </div>
          </div>
        </>
      )}

      {form.recipientMode === "all" && (
        <div className="sx-all-recipients-card">
          <h3>All active contacts selected</h3>

          <p>
            This campaign will be sent to all active and subscribed contacts.
            Unsubscribed, bounced, and spam-marked contacts will be excluded.
          </p>

          <strong>{selectedCount} contacts ready</strong>
        </div>
      )}

      {openList && (
        <ListContactsModal
          list={openList}
          listContacts={modalListContacts}
          excludedIds={excludedContacts}
          onToggleContact={toggleExcludeContact}
          onEnableAll={() => enableAllInList(openList)}
          onDisableAll={() => disableAllInList(openList)}
          onClose={() => setOpenList(null)}
        />
      )}
    </div>
  );
}
